import React, { useState, useEffect } from 'react';
import './DashboardSidebar.css';
import { BACKEND_URL } from '../utils/config';

/**
 * DashboardSidebar Component
 * Shared sidebar component for all three dashboards (Patient, ServiceWorker, Admin)
 * 
 * Props:
 * - sidebarCollapsed: Boolean indicating if sidebar is collapsed
 * - onToggleSidebar: Callback to toggle sidebar collapse state
 * - mobileMenuOpen: Boolean indicating if mobile menu is open (optional, for PatientDashboard and ServiceWorkerDashboard)
 * - onToggleMobileMenu: Callback to toggle mobile menu (optional)
 * - profileTitle: Title text for the profile section (e.g., "Welcome Back, John" or "Service Worker - John")
 * - profileSubtitle: Subtitle text for the profile section
 * - buttons: Array of button objects with the following structure:
 *   - icon: String emoji or SVG (e.g., "📊")
 *   - text: String button label (e.g., "View Reports")
 *   - onClick: Function to call when button is clicked
 *   - className: Optional CSS class (e.g., "logout-button")
 *   - ariaLabel: Optional aria-label for accessibility
 *   - title: Optional title attribute for tooltip
 */
export default function DashboardSidebar({
    sidebarCollapsed,
    onToggleSidebar,
    mobileMenuOpen,
    onToggleMobileMenu,
    profileTitle,
    profileSubtitle,
    buttons = []
}) {
    const [profileImage, setProfileImage] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState(null);
    const fileInputRef = React.useRef(null);

    const fetchProfileImage = async () => {
        try {
            setImageLoading(true);
            setImageError(false);
            
            const response = await fetch(`${BACKEND_URL}/users/upload`, {
                credentials: 'include',
            });

            if (!response.ok) {
                setImageError(true);
                return;
            }

            // Get the image blob and convert to data URL
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setProfileImage(url);
        } catch (error) {
            console.error('Error fetching profile image:', error);
            setImageError(true);
        } finally {
            setImageLoading(false);
        }
    };

    useEffect(() => {
        fetchProfileImage();

        // Cleanup object URL on unmount
        return () => {
            if (profileImage) {
                URL.revokeObjectURL(profileImage);
            }
        };
    }, []);

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            setUploadMessage({ type: 'error', text: 'Please upload a JPG or PNG image only' });
            setTimeout(() => setUploadMessage(null), 3000);
            return;
        }

        try {
            setUploadLoading(true);
            setUploadMessage(null);

            // Determine Content-Type based on file type
            const contentType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

            // Send file to backend
            const response = await fetch(`${BACKEND_URL}/users/upload`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': contentType,
                },
                body: file,
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.status}`);
            }

            setUploadMessage({ type: 'success', text: 'Profile image uploaded successfully!' });
            
            // Refetch the profile image after a few seconds to get the updated image from server
            setTimeout(() => {
                fetchProfileImage();
            }, 3000);
            
            setTimeout(() => setUploadMessage(null), 3000);
        } catch (error) {
            console.error('Error uploading profile image:', error);
            setUploadMessage({ type: 'error', text: 'Failed to upload image. Please try again.' });
            setTimeout(() => setUploadMessage(null), 3000);
        } finally {
            setUploadLoading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    return (
        <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
            {/* Sidebar Toggle Button - Always at top left */}
            <button 
                className="sidebar-toggle"
                onClick={onToggleSidebar}
                aria-label="Toggle sidebar"
            >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {sidebarCollapsed ? (
                        <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                        <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                </svg>
            </button>

            {/* Profile Section */}
            <div className="profile-section">
                <div className="profile-image-container">
                    <div className="profile-image">
                        {profileImage && !imageError ? (
                            <img 
                                src={profileImage} 
                                alt="Profile" 
                                className="profile-image-img"
                                onError={() => setImageError(true)}
                            />
                        ) : (
                            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="40" cy="40" r="40" fill="#e2e8f0"/>
                                <circle cx="40" cy="32" r="12" fill="#94a3b8"/>
                                <path d="M20 60C20 52 28 48 40 48C52 48 60 52 60 60V64H20V60Z" fill="#94a3b8"/>
                            </svg>
                        )}
                    </div>
                    <button
                        className="upload-image-button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadLoading}
                        title="Upload profile image"
                        aria-label="Upload profile image"
                    >
                        <span className="upload-icon">📷</span>
                    </button>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/jpg,image/png"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    disabled={uploadLoading}
                />
                {uploadMessage && (
                    <div className={`upload-message ${uploadMessage.type}`}>
                        {uploadMessage.text}
                    </div>
                )}
                <h2 className="profile-name">{profileTitle}</h2>
                <p className="profile-subtitle">{profileSubtitle}</p>
            </div>

            {/* Mobile Menu Toggle */}
            {onToggleMobileMenu && (
                <button 
                    className="mobile-menu-toggle"
                    onClick={onToggleMobileMenu}
                    aria-label="Toggle menu"
                    aria-expanded={mobileMenuOpen}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        {mobileMenuOpen ? (
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        ) : (
                            <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        )}
                    </svg>
                </button>
            )}

            {/* Sidebar Action Buttons */}
            <div className={`sidebar-actions ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                {buttons.map((button, index) => (
                    <button
                        key={index}
                        className={`sidebar-button ${button.className || ''}`}
                        onClick={button.onClick}
                        aria-label={button.ariaLabel}
                        title={button.title}
                    >
                        <span className="button-icon">{button.icon}</span>
                        <span className="button-text">{button.text}</span>
                    </button>
                ))}
            </div>
        </aside>
    );
}
